/**
 * 标签单选组件（SingleSelectChips）
 * 美观的标签式单选，支持多种选项展示
 */

import type { ReactElement } from 'react'
import { Check } from 'lucide-react'
import { isSharedUserOptionAvailable } from '@/types/workflow'
import { cn } from '@/lib/utils'
import type { FormFieldProps } from './types'

const DEFAULT_RESTRICTED_OPTION_MESSAGE = '该模式耗费大量算力，仅对专享用户开放'

/**
 * 获取选项的限制提示文案
 * @param isRestricted 选项是否受限
 * @param restrictedOptionMessage 自定义限制文案
 * @returns 选项提示文案
 */
function getOptionTitle(
  isRestricted: boolean,
  restrictedOptionMessage: string
): string | undefined {
  return isRestricted ? restrictedOptionMessage : undefined
}

/**
 * 标签单选组件
 * @param props 组件属性
 * @returns 组件元素
 */
export function SingleSelectChipsField({
  component,
  value,
  onChange,
  error,
  disabled = false,
  restrictHighCostOptions = false,
  restrictedOptionMessage = DEFAULT_RESTRICTED_OPTION_MESSAGE,
}: FormFieldProps): ReactElement {
  const options = component.options ?? []
  const hasRestrictedOptions = restrictHighCostOptions &&
    options.some((option) => !isSharedUserOptionAvailable(option))

  return (
    <div className="space-y-3">
      {/* 标题 */}
      <label className="block text-sm font-medium text-foreground">
        {component.title}
        {component.is_required && (
          <span className="ml-1 text-destructive">*</span>
        )}
      </label>

      {/* 选项区域 */}
      <div className="flex flex-wrap gap-2.5">
        {options.map((option) => {
          const isRestricted = restrictHighCostOptions && !isSharedUserOptionAvailable(option)
          const isActive = !isRestricted && value === option.value
          const optionTitle = getOptionTitle(isRestricted, restrictedOptionMessage)
          return (
            <div
              key={String(option.value)}
              className="inline-flex"
              title={optionTitle}
            >
              <button
                type="button"
                disabled={disabled || isRestricted}
                aria-disabled={disabled || isRestricted}
                aria-label={optionTitle !== undefined ? `${option.label}，${optionTitle}` : option.label}
                onClick={(): void => {
                  onChange(option.value)
                }}
                className={cn(
                  'inline-flex items-center gap-1.5 rounded-full px-4 py-2 text-sm font-medium transition-all',
                  'border focus:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2',
                  isActive
                    ? 'border-primary bg-primary text-primary-foreground shadow-sm'
                    : 'border-border bg-background text-muted-foreground hover:border-primary/50 hover:bg-muted/50 hover:text-foreground',
                  isRestricted && [
                    'cursor-not-allowed border-amber-500/30 bg-amber-500/5 text-muted-foreground',
                    'hover:border-amber-500/30 hover:bg-amber-500/5 hover:text-muted-foreground',
                  ],
                  disabled && !isRestricted && 'cursor-not-allowed opacity-50'
                )}
              >
                {/* 选中图标 */}
                {isActive && (
                  <Check className="h-3.5 w-3.5" />
                )}
                <span>{option.label}</span>
                {/* 计费权重标识 */}
                {option.billing_weight !== undefined && option.billing_weight !== 1 && (
                  <span className={cn(
                    'ml-0.5 text-xs',
                    isActive ? 'opacity-80' : 'opacity-60'
                  )}>
                    ×{String(option.billing_weight)}
                  </span>
                )}
                {isRestricted && (
                  <span className="rounded-full border border-amber-500/30 bg-amber-500/10 px-1.5 py-0.5 text-[10px] leading-none text-amber-700 dark:text-amber-300">
                    专享可用
                  </span>
                )}
              </button>
            </div>
          )
        })}
      </div>

      {hasRestrictedOptions && (
        <p className="text-xs leading-relaxed text-muted-foreground">
          {restrictedOptionMessage}
        </p>
      )}

      {/* 错误提示 */}
      {error !== undefined && (
        <p className="text-xs text-destructive">{error}</p>
      )}
    </div>
  )
}
